import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Sparkles, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Monthly Bulletin banner for /shop.
 *
 * Auto-reads the currently published price ledger (August 2026 today,
 * flips to September 2026 the moment the admin publishes next month's
 * PDF). Renders a monumental, animated copper-glow button that scrolls
 * the visitor to the Home page bulletin.
 */
export function MonthlyBulletinButton() {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    axios.get(`${API}/price-ledger/latest`)
      .then((r) => setLedger(r.data?.ledger || null))
      .catch(() => setLedger(null));
  }, []);
  if (!ledger) return null;
  const s = ledger.summary || {};

  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-4 pb-4">
      <Link
        to="/rates-bulletin"
        data-testid="monthly-bulletin-button"
        className="group relative block overflow-hidden rounded-sm border border-[#D8B23E]/50"
        style={{
          background:
            "linear-gradient(115deg, rgba(184,115,51,0.16) 0%, rgba(216,178,62,0.10) 40%, rgba(10,10,10,0.9) 100%)",
        }}
      >
        {/* animated shimmer sweep */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)",
          }}
        />
        {/* pulsing dot ring corners */}
        <span className="absolute top-2 left-2 w-2 h-2 rounded-full bg-[#D8B23E] animate-pulse" />
        <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#B87333] animate-pulse" />
        <span className="absolute bottom-2 left-2 w-2 h-2 rounded-full bg-[#B87333] animate-pulse" />
        <span className="absolute bottom-2 right-2 w-2 h-2 rounded-full bg-[#D8B23E] animate-pulse" />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-wrap items-center justify-between gap-6 px-6 md:px-10 py-6"
        >
          <div className="flex items-center gap-5 min-w-0">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-[#D8B23E]/20 text-[#D8B23E] shrink-0 shadow-[0_0_28px_rgba(216,178,62,0.35)]">
              <Sparkles size={20} />
            </span>
            <div className="min-w-0">
              <div
                className="uppercase text-[10px] tracking-[0.32em] text-[#D8B23E]"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
              >
                Official Bulletin · {ledger.month_label}
              </div>
              <div
                data-testid="monthly-bulletin-headline"
                className="text-[#F5F5F0] mt-1 leading-none"
                style={{
                  fontFamily: "'Cinzel', 'Playfair Display', serif",
                  fontWeight: 700,
                  fontSize: "clamp(1.5rem, 3.2vw, 2.6rem)",
                  letterSpacing: "0.02em",
                }}
              >
                <span className="bg-gradient-to-r from-[#F5F5F0] to-[#E6C88A] bg-clip-text text-transparent">
                  {ledger.month_label} · Rates at a Glance
                </span>
              </div>
              <div className="font-body text-xs text-[#8A8A82] mt-2">
                {(s.brands || []).length} brands · {s.total || 0} verified SKUs · steel &amp; cement
              </div>
            </div>
          </div>
          <span
            className="inline-flex items-center gap-2 rounded-full bg-[#D8B23E] text-black px-6 py-3 font-grotesk text-[11px] uppercase tracking-[0.24em] shadow-[0_0_32px_rgba(216,178,62,0.4)] group-hover:brightness-110 transition-all"
            data-testid="monthly-bulletin-cta"
          >
            Monthly Bulletin
            <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
          </span>
        </motion.div>
      </Link>
    </section>
  );
}
